import { createFileRoute } from '@tanstack/react-router';
import { LiveboardEmbed as LiveboardEmbedType } from '@thoughtspot/visual-embed-sdk';
import { LiveboardEmbed, useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { RefObject } from 'react';

interface RouteParams {
  object_id: string;
  sub_object_id: string;
}
interface ParsedParams {
  object_id: string;
  sub_object_id: string;
}

/**
 * DEEP LINK ROUTE: Liveboard Visualization Viewer
 *
 * This route serves as a direct access point for sharing/bookmarking specific visualizations
 * within a ThoughtSpot liveboard (dashboard).
 * Users typically land here via:
 * - Shared visualization links within liveboards
 * - Email links containing specific chart/viz URLs
 * - Bookmarks to specific visualizations
 * - External integrations
 *
 * URL Pattern: /dashboard/pinboard/{object_id}/{sub_object_id}
 * Example: /dashboard/pinboard/d084c256-e284-4fc4-b80c-111cb606449a/df5440f9-7065-4535-9748-cfc57f8a5f3e
 *
 * This route uses a direct LiveboardEmbed component with its own independent iframe,
 * displaying a specific visualization (vizId) from a liveboard.
 */
export const Route = createFileRoute('/dashboard/_deep-links/pinboard/$object_id/$sub_object_id')<{
  params: RouteParams;
}>({
  component: RouteComponent,
  params: {
    parse: (params: Record<string, string>): ParsedParams => {
      const { object_id, sub_object_id } = params;

      return {
        object_id,
        sub_object_id,
      };
    },
  },
  beforeLoad: ({ context }) => {
    const { isAuthenticated, isLoading } = context.auth;

    if (!isLoading && !isAuthenticated) {
      return; // AuthProvider will handle login redirect
    }
  },
});

function RouteComponent() {
  const ref = useEmbedRef() as RefObject<LiveboardEmbedType>;
  const { object_id, sub_object_id } = Route.useParams();

  return (
    <LiveboardEmbed
      showLiveboardTitle={true}
      hiddenActions={[]}
      ref={ref}
      liveboardV2={true}
      isLiveboardMasterpiecesEnabled={true}
      // TODO: waiting for TS support verification whether the flag also needs to be explicitly passed on those deep-link routes
      // enableCustomColumnGroups={true}
      // additionalFlags={{ enableCustomColumnGroups: true }}
      liveboardId={object_id}
      vizId={sub_object_id}
      className="h-[calc(100vh-var(--dashboard-menu-height))] w-full"
    />
  );
}
